import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { IUserTimelineItem } from '@/api/user/getUserDailyTimeline.ts'
import {
    accuracyLabel,
    formatClock,
    hasTimelineLocation,
    summarizeTimeline,
    timelineKindLabel,
    unavailableLabel
} from '@/util/positionTimeline.ts'

export interface IProps {
    items: IUserTimelineItem[]
    activeIndex: number | null
    onSelect: (index: number) => void
}

const PinOffIcon: React.FC = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 21c-3.5-3.2-6-6.2-6-9.5a6 6 0 0 1 12 0c0 3.3-2.5 6.3-6 9.5z" />
        <path d="M4 4l16 16" />
    </svg>
)

const PositionTimelinePanel: React.FC<IProps> = ({ items, activeIndex, onSelect }) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState<boolean>(true)
    const listRef = useRef<HTMLOListElement>(null)
    const summary = summarizeTimeline(items)

    useEffect(() => {
        const list = listRef.current
        if (!isOpen || activeIndex === null || !list) return

        const row = list.children[activeIndex] as HTMLElement | undefined
        if (!row) return

        const top = row.offsetTop
        const bottom = top + row.offsetHeight
        if (top < list.scrollTop) {
            list.scrollTop = top
        } else if (bottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = bottom - list.clientHeight
        }
    }, [activeIndex, isOpen, items])

    return (
        <aside className="member-route-timeline" aria-label={t('users.positionTimeline.ariaLabel')}>
            <div className="member-route-timeline__header">
                <div>
                    <strong>{t('users.positionTimeline.title')}</strong>
                    <span>
                        {t('users.positionTimeline.total', { value: summary.total.toLocaleString() })}
                        {summary.unavailable > 0 &&
                            ` · ${t('users.positionTimeline.unavailableCount', { value: summary.unavailable.toLocaleString() })}`}
                    </span>
                </div>
                <button
                    type="button"
                    className="member-route-timeline__toggle"
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen(open => !open)}
                >
                    {isOpen ? t('dashboard.bioAlertList.collapse') : t('dashboard.bioAlertList.expand')}
                </button>
            </div>

            {isOpen && (
                <ol className="member-route-timeline__list" ref={listRef}>
                    {items.map((item, index) => {
                        const isActive = index === activeIndex
                        const kindLabel = timelineKindLabel(item.kind, t)
                        return (
                            <li key={`${item.order}-${index}`}>
                                <button
                                    type="button"
                                    className={`member-route-timeline__row${isActive ? ' is-active' : ''}`}
                                    aria-current={isActive ? 'true' : undefined}
                                    onClick={() => onSelect(index)}
                                >
                                    <span className="member-route-timeline__index">{index + 1}</span>
                                    <time className="member-route-timeline__time" dateTime={item.timestamp}>
                                        {formatClock(item.timestamp)}
                                    </time>
                                    {kindLabel && (
                                        <span className={`member-route-timeline__kind is-${item.kind}`}>
                                            {kindLabel}
                                        </span>
                                    )}
                                    {hasTimelineLocation(item) ? (
                                        <span className="member-route-timeline__accuracy">
                                            {accuracyLabel(item.location.accuracy)}
                                        </span>
                                    ) : (
                                        <span className="member-route-timeline__reason">
                                            <PinOffIcon />
                                            {unavailableLabel(item.unavailableReason, t)}
                                        </span>
                                    )}
                                </button>
                            </li>
                        )
                    })}
                </ol>
            )}
        </aside>
    )
}

export default PositionTimelinePanel
